// api
import type { ReadUnitDto } from '@/api/generated/recalc/endpoints/v1/units';
import type {
  AssociationsDto,
  DataStreamAssociation,
} from '@/api/generated/recalc/endpoints/v2/alarmConfiguration';

// relative
import type { DataStreamOption } from './types';

export function normalizeDataStreamOptions(
  units: ReadUnitDto[] = [],
): DataStreamOption[] {
  return units.flatMap((unit) => {
    const unitId = unit.UnitId;
    const unitName = unit.Name ?? String(unit.UnitId);
    const unitTags = Array.from(
      new Set(
        (unit.Tags ?? [])
          .map((tag) => tag.Text)
          .filter((tagText): tagText is string => Boolean(tagText)),
      ),
    );

    return (unit.RecordedDataTypes ?? []).flatMap((recordedDataType) => {
      const unitTypeInputId = recordedDataType.UnitTypeInput?.UnitTypeInputId;

      if (typeof unitTypeInputId !== 'number') {
        return [];
      }

      return (recordedDataType.DataTypes ?? []).map((dataType) => ({
        key: `${unitId}:${unitTypeInputId}:${dataType}`,
        unitId,
        unitName,
        unitTags,
        unitTypeInputId,
        dataType,
      }));
    });
  });
}

export function getDataStreamAssociationFromOption(
  option: DataStreamOption,
): DataStreamAssociation {
  return {
    unitId: option.unitId,
    unitTypeInputId: option.unitTypeInputId,
    dataType: option.dataType,
  };
}

export function getDataStreamAssociationsFromKeys(
  dataStreamOptions: DataStreamOption[],
  selectedDataStreamKeys: string[] = [],
): DataStreamAssociation[] {
  const selectedKeys = new Set(selectedDataStreamKeys);

  return dataStreamOptions
    .filter((option) => selectedKeys.has(option.key))
    .map(getDataStreamAssociationFromOption);
}

function normalizeDataStreamAssociation(
  dataStream: DataStreamAssociation,
): DataStreamAssociation {
  return {
    ...dataStream,
    unitTypeInputId:
      dataStream.unitTypeInputId === 0
        ? (null as unknown as number)
        : dataStream.unitTypeInputId,
  };
}

export function buildAssociationsPayload(
  units: ReadUnitDto[],
  selectedDataStreams: DataStreamAssociation[] = [],
): AssociationsDto {
  const normalizedDataStreams = selectedDataStreams.map(
    normalizeDataStreamAssociation,
  );

  const selectedUnitIds = Array.from(
    new Set(
      normalizedDataStreams
        .map((dataStream) => dataStream.unitId)
        .filter((unitId): unitId is number => unitId != null),
    ),
  );

  return {
    units: selectedUnitIds.map((unitId) => {
      const selectedUnit = units.find((unit) => unit.UnitId === unitId);

      return {
        id: unitId,
        name: selectedUnit?.Name ?? null,
      };
    }),
    buildings: [],
    jobSubscriptions: [],
    sensors: [],
    gateways: [],
    energyGroups: [],
    energyInitiatives: [],
    energyTreeNodes: [],
    dataStreams: normalizedDataStreams,
  };
}
